import { AxiosResponse } from 'axios';
import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

export type GA4Property = {
  propertyId: string;
  displayName: string;
  account: string;
};

export type GA4OverviewMetrics = {
  activeUsers: number;
  sessions: number;
  bounceRate: number;
  avgSessionDuration: number;
  conversions: number;
  newUsers: number;
};

export type GA4ChannelRow = {
  channel: string;
  sessions: number;
  users: number;
  conversions: number;
};

export type GA4DashboardSummary = {
  current: GA4OverviewMetrics;
  previous: GA4OverviewMetrics;
  channels: GA4ChannelRow[];
};

async function invokeGaFunction(body: Record<string, unknown>): Promise<any> {
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  const { data, error } = await supabase.functions.invoke('google-analytics', {
    body: { ...body, userId },
  });

  if (error) throw error;
  return data;
}

const googleAnalyticsService = {
  async listProperties(): Promise<AxiosResponse<GA4Property[]>> {
    const data = await invokeGaFunction({ action: 'listProperties' });
    return asResponse(data.properties ?? []);
  },

  async getDashboardSummary(propertyId: string, days = 28): Promise<AxiosResponse<GA4DashboardSummary>> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    const prevEndDate = new Date(startDate);
    prevEndDate.setDate(prevEndDate.getDate() - 1);
    const prevStartDate = new Date(prevEndDate);
    prevStartDate.setDate(prevEndDate.getDate() - days);

    const fmt = (d: Date) => d.toISOString().split('T')[0];

    const data = await invokeGaFunction({
      action: 'dashboardSummary',
      propertyId,
      startDate: fmt(startDate),
      endDate: fmt(endDate),
      prevStartDate: fmt(prevStartDate),
      prevEndDate: fmt(prevEndDate),
    });

    return asResponse(data as GA4DashboardSummary);
  },
};

export default googleAnalyticsService;
